import { axiosInstance, http } from '@shared/api/http-client';
import type {
  AssignSubscriptionInput,
  ListUsersParams,
  PageMeta,
  PlatformUser,
  Subscription,
  SubscriptionPlanOption,
  UsersPage,
} from '@features/users/model/user.types';

interface ListResponse {
  data: PlatformUser[];
  meta: PageMeta;
}

/**
 * The list endpoint returns paging information in the envelope's `meta`, which
 * the shared `http` helper drops, so this one call uses the axios instance
 * directly to keep both halves.
 */
export const userApi = {
  async list(params: ListUsersParams): Promise<UsersPage> {
    const response = await axiosInstance.get<ListResponse>('/budgeting/users', {
      params: {
        page: params.page,
        pageSize: params.pageSize,
        ...(params.search ? { search: params.search } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.plan ? { plan: params.plan } : {}),
      },
    });

    return { users: response.data.data, meta: response.data.meta };
  },

  async statuses(): Promise<string[]> {
    const response = await axiosInstance.get<{ data: string[] }>('/budgeting/users/statuses');

    return response.data.data;
  },

  async plans(): Promise<SubscriptionPlanOption[]> {
    const response = await axiosInstance.get<{ data: SubscriptionPlanOption[] }>(
      '/budgeting/users/plans',
    );

    return response.data.data;
  },

  /**
   * Puts a user on a plan. Optional dates are omitted rather than sent blank, so
   * the server applies its own defaults instead of parsing empty strings.
   */
  async assignSubscription(
    userId: string,
    input: AssignSubscriptionInput,
  ): Promise<Subscription> {
    return http.post<Subscription>(`/budgeting/users/${userId}/subscriptions`, {
      planId: input.planId,
      status: input.status,
      ...(input.startsAt ? { startsAt: input.startsAt } : {}),
      ...(input.endsAt ? { endsAt: input.endsAt } : {}),
      ...(input.trialEndsAt ? { trialEndsAt: input.trialEndsAt } : {}),
    });
  },
};
